import { createTheme } from '@mui/material/styles';
import type { PaletteColorOptions } from '@mui/material/styles';

export interface ICustomPalette {
  white: string;
  green: string;
  greenDark: string;
  greenLight: string;
  yellow: string;
  greyLighter: string;
  greyLight: string;
  greyMedium: string;
  greyDark: string;
  darkBlack: string;
  lightRed: string;
  mediumRed: string;
  ruby: string;
  darkBlack1: string;
  errorRed: string;
  grey1: string;
  grey2: string;
  grey3: string;
  grey4: string;
  grey5: string;
  grey6: string;
  blue: string;
  black: string;
  greyDark1: string;
}

declare module '@mui/material/styles' {
  interface Palette {
    custom: ICustomPalette;
    yellow: Palette['primary'];
    orange: Palette['primary'];
    red: Palette['primary'];
  }

  interface PaletteOptions {
    custom?: ICustomPalette;
    yellow?: PaletteColorOptions;
    orange?: PaletteColorOptions;
    red?: PaletteColorOptions;
  }

  interface PaletteColor {
    [key: number]: string;
    [key: string]: string;
  }

  interface SimplePaletteColorOptions {
    [key: number]: string;
    [key: string]: string;
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    yellow: true;
  }
}

export const extraColor = {
  palette: {
    green: {
      main: '#1a551f',
      light: '#e5f0da',
      dark: '#c1eb98',
    },
    error: {
      primary: '#f88a86',
      secondary: '#f44336cf',
      light: '#e42424',
    },
    blue: {
      light: '#e9f2f7',
      main: '#a3d0e4',
      '500': '#0089fe',
    },
    grey: {
      light: '#efefef',
      main: '#d8d8d8',
      dark: '#8484841c',
      '400': '#0000004d',
      offwhite: '#e9e8ee',
    },
    transparent: {
      light: '#6d6d6d00',
    },
    orange: {
      dark: '#dc6601',
      main: '#f1b047',
      light: '#f5c77e',
      '400': '#ffc107',
    },
  },
};

/* =========================
   BASE THEME
========================= */

const colorTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#00AA8F',
      light: '#33bba5',
      dark: '#007f6b',
      contrastText: '#ffff',
    },
    secondary: {
      main: '#C3C3C3',
      light: '#F2F2F2',
      dark: '#7B838A',
    },
    info: {
      main: '#3684B0',
      light: '#FFBD54',
      dark: '#E26943',
    },
    error: {
      main: '#d32f2f',
      light: '#f4b8b6',
      200: '#f88a86',
      contrastText: '#ffff',
    },
    success: {
      main: '#67a12f',
      dark: '#578528',
      light: '#e5f0da',
      '600': '#1a551f',
    },
    text: {
      primary: '#000000',
      secondary: '#2D2D2D',
    },
    common: {
      white: '#fff',
      black: '#000',
    },
    grey: {
      400: '#e1e1e1',
      500: '#dddddd',
      700: '#949494',
    },
    yellow: {
      main: '#fabb65',
    },
    orange: {
      main: '#f1b047',
      light: '#f5c77e',
      dark: '#dc6601',
      200: '#FF851B',
      400: '#ffc107',
      50: '#FFF5E6',
    },
    red: {
      main: '#FF0000',
      200: '#FF0000',
    },
    custom: {
      white: '#ffffff',
      green: '#00aa8f',
      greenDark: '#006838',
      greenLight: '#19a329',
      yellow: '#f0bf3f',
      greyLighter: '#cbcbcb',
      greyLight: '#e6e6e6',
      greyMedium: '#8c9091',
      greyDark: '#4b4b4b',
      darkBlack: '#222222',
      lightRed: '#ff0000',
      mediumRed: '#c92424',
      ruby: '#7b1b17',
      darkBlack1: '#181a1a',
      errorRed: '#e2423c',
      grey1: '#e1e2e2',
      grey2: '#cad5dd',
      grey3: '#f4f6f8',
      grey4: '#a7a9aa',
      grey5: '#f9f9f9',
      grey6: '#7c7e7e',
      blue: '#2c5a7a',
      black: '#000000',
      greyDark1: '#49454f',
    },
  },
  spacing: 8,
  drawerWidth: '260px',
  typography: {
    fontFamily: '"Open Sans", "Roboto", "Helvetica", "Arial", sans-serif',
  },
});

export const muiTheme = createTheme(colorTheme, {
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          fontSize: '13px',
          lineHeight: '1.3',
          '@page': {
            size: 'auto',
            marginTop: '0.5cm',
          },
        },
        span: {
          fontSize: '13px',
          lineHeight: '1.3',
        },
        div: {
          fontSize: '13px',
          lineHeight: '1.3',
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          gap: '10px',
          display: 'flex',
          alignItems: 'center',
          fontSize: 12,
        },
        h1: {
          fontSize: 22,
          fontWeight: 500,
          lineHeight: '20px',
        },
        h2: {
          fontSize: 20,
          fontWeight: 400,
          lineHeight: 1.3,
        },
        h3: {
          fontSize: 18,
          fontWeight: 400,
          marginBottom: '10px',
          lineHeight: 1.3,
        },
        h4: {
          fontSize: 16,
          fontWeight: 400,
        },
        body1: {
          fontSize: 13,
          fontWeight: 400,
        },
        subtitle2: {},
        subtitle1: {
          fontSize: 14,
          fontWeight: 400,
        },
        caption: {
          color: `${colorTheme.palette.common.black}`,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 700,
          minHeight: '30px',
          boxShadow: 'none',
        },
        containedPrimary: {
          backgroundColor: `${colorTheme.palette.primary.light}`,
        },
        containedError: {
          color: `${colorTheme.palette.common.white}`,
        },
        containedInfo: {
          color: `${colorTheme.palette.common.white}`,
          background: `${colorTheme.palette.info.dark}`,
          '&:hover': {
            backgroundColor: `${colorTheme.palette.info.dark}`,
          },
        },
        textInfo: {
          color: `${colorTheme.palette.info.dark}`,
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          fontSize: 12,
          fontWeight: 600,
          color: `${colorTheme.palette.primary.main}`,
          borderRadius: 0,
          display: 'flex',
          justifyContent: 'center',
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: `${colorTheme.palette.secondary.main}`,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          letterSpacing: '.445px',
          textTransform: 'none',
          '&.Mui-selected': {
            fontWeight: 700,
            letterSpacing: 0,
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          border: 'none',
        },
        indicator: {
          height: '4px',
          backgroundColor: `${colorTheme.palette.info.light}`,
          transition: 'all 100ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
        },
        scrollButtons: {
          '&.Mui-disabled': {
            display: 'none',
          },
        },
      },
    },

    MuiPopover: {
      defaultProps: {
        elevation: 3,
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          overflow: 'hidden',
          fontSize: 12,
          paddingTop: 0,
          paddingBottom: 0,
          fontWeight: 400,
          border: 0,
          borderBottom: `1px solid ${colorTheme.palette.secondary.main}`,
          borderRadius: 0,
          '&.Mui-disabled': {
            background: `${colorTheme.palette.secondary.main}`,
          },
        },
        input: {
          background: `${colorTheme.palette.common.white}`,
          color: `${colorTheme.palette.primary.main}`,
          borderRadius: 0,
          '&.Mui-disabled': {
            background: 'transparent',
            WebkitTextFillColor: `${colorTheme.palette.primary.main}`,
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          bottom: 0,
          margin: 0,
          position: 'absolute',
          transform: 'translateY(100%)',
        },
      },
    },
    MuiGrid: {
      styleOverrides: {
        root: {
          '&.MuiGrid-container': {
            marginBottom: 10,
          },
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          height: '100%',
        },
        label: {
          fontWeight: 100,
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          height: '2px',
        },
      },
    },
  },
});

/* =========================
   SUB-THEMES
========================= */

export const assetSelectTheme = createTheme({
  palette: {
    primary: { main: '#7b1b17' },
    secondary: { main: '#7b1b17' },
  },
});

export const yellowButtonTheme = createTheme({
  palette: {
    primary: { main: '#f0bf3f' },
    secondary: { main: '#7b1b17' },
  },
  typography: {
    fontSize: 22,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        containedPrimary: {
          color: 'white',
        },
      },
    },
  },
});

export const stipsSelectTheme = createTheme({
  palette: {
    primary: {
      main: '#181a1a',
    },
    secondary: {
      main: '#181a1a',
    },
  },
});
